/**
 * FocusTrap Component (Task 10.5)
 *
 * Traps focus within a container for modal dialogs
 * Ensures keyboard navigation stays within the active modal
 */

import { useEffect, useRef, type ReactNode } from 'react';

interface FocusTrapProps {
  /**
   * Children to render inside the focus trap
   */
  children: ReactNode;

  /**
   * Whether the focus trap is active
   * @default true
   */
  active?: boolean;

  /**
   * Optional initial focus element selector
   */
  initialFocus?: string;
}

export function FocusTrap({
  children,
  active = true,
  initialFocus,
}: FocusTrapProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!active || !containerRef.current) return;

    const container = containerRef.current;

    // Get all focusable elements within the container
    const getFocusableElements = (): HTMLElement[] => {
      const selector =
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
      return Array.from(container.querySelectorAll(selector));
    };

    // Set initial focus
    const initialElement = initialFocus
      ? container.querySelector<HTMLElement>(initialFocus)
      : null;

    if (initialElement) {
      initialElement.focus();
    } else {
      const focusableElements = getFocusableElements();
      if (focusableElements.length > 0) {
        focusableElements[0]?.focus();
      }
    }

    // Handle Tab key navigation
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      const focusableElements = getFocusableElements();
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (!firstElement || !lastElement) return;

      // Shift + Tab: move backward
      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      }
      // Tab: move forward
      else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    container.addEventListener('keydown', handleKeyDown);

    return () => {
      container.removeEventListener('keydown', handleKeyDown);
    };
  }, [active, initialFocus]);

  return (
    <div ref={containerRef} className="focus-trap-container">
      {children}
    </div>
  );
}
